import { useState } from "react";
import { CalendarEvent } from "../../../api/calendarEvent";
import KlydoEventModal from "./KlydoEventModal";

import DSBox from "../../system-design/DSBox";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSCard from "../../system-design/DSCard";

interface KlydoEventDayModalProps {
  open: boolean;
  onClose: () => void;
  date: Date;
  events: CalendarEvent[];
}

function KlydoEventDayModal({
  open,
  onClose,
  date,
  events,
}: KlydoEventDayModalProps) {
  const [editingEvent, setEditingEvent] = useState<CalendarEvent>();
  const [createNew, setCreateNew] = useState(false);

  return (
    <>
      <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DSDialogTitle>
          Klydo Events - {date.toLocaleDateString()}
        </DSDialogTitle>
        <DSDialogContent dividers sx={{ p: 2 }}>
          <DSBox
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {events.map((event) => (
              <DSCard
                key={event.id}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                onClick={() => setEditingEvent(event)}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <DSBox>
                    <DSTypography variant="subtitle1" fontWeight="bold">
                      {event.name || "Unnamed Event"}
                    </DSTypography>
                    <DSTypography variant="body2" color="text.secondary">
                      {event.klydoIds?.length || 0} klydo
                      {(event.klydoIds?.length || 0) !== 1 ? "s" : ""}
                      {!event.date.year && " (recurring yearly)"}
                    </DSTypography>
                  </DSBox>
                  <DSBox sx={{ display: "flex", gap: 0.5 }}>
                    {event.overrideOtherEvents && (
                      <DSChip
                        label="Override"
                        size="small"
                        color="warning"
                        variant="filled"
                      />
                    )}
                  </DSBox>
                </DSBox>
              </DSCard>
            ))}
          </DSBox>
        </DSDialogContent>
        <DSDialogActions>
          <DSPrimaryButton
            variant="outlined"
            onClick={() => setCreateNew(true)}
          >
            + New Event
          </DSPrimaryButton>
          <DSPrimaryButton variant="outlined" onClick={onClose}>
            Close
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>

      {editingEvent && (
        <KlydoEventModal
          open={!!editingEvent}
          onClose={() => {
            setEditingEvent(undefined);
            onClose();
          }}
          event={editingEvent}
          date={date}
        />
      )}

      {createNew && (
        <KlydoEventModal
          open={createNew}
          onClose={() => {
            setCreateNew(false);
            onClose();
          }}
          date={date}
        />
      )}
    </>
  );
}

export default KlydoEventDayModal;
